import { buildPublicUrl } from '@shared/config/env';
import type {
  InvoiceDto,
  InvoiceStatsDto,
  InvoiceTransactionAmlDto,
  InvoiceTransactionDto,
} from '@shared/api/contracts';
import type {
  Invoice,
  InvoiceStats,
  InvoiceTransaction,
  InvoiceTransactionAml,
} from '../model/types';

const mapAmlDto = (aml?: InvoiceTransactionAmlDto | null): InvoiceTransactionAml | null => {
  if (!aml) return null;

  return {
    id: aml.id != null ? String(aml.id) : undefined,
    transactionId: aml.transactionId != null ? String(aml.transactionId) : undefined,
    riskscore: aml.riskscore,
    riskyVolume: aml.risky_volume,
    signals: aml.signals,
    executed: aml.executed,
    isPending: aml.is_pending,
    isOk: aml.is_ok,
    checkAt: aml.check_at,
    pdf: aml.pdf,
  };
};

const normalizeApiDate = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    return new Date(ms).toISOString();
  }
  return String(value);
};

const mapTransactionDto = (dto: InvoiceTransactionDto): InvoiceTransaction => ({
  txid: dto.txid,
  category: dto.category,
  amount: Number(dto.amount),
  addressFrom: dto.addressFrom,
  addressTo: dto.addressTo,
  time: normalizeApiDate(dto.time),
  status: dto.status,
  asset: dto.asset,
  network: dto.network,
  aml: mapAmlDto(dto.aml),
});

export const mapInvoiceDtoToEntity = (dto: InvoiceDto): Invoice => ({
  id: String(dto.id),
  projectId: String(dto.projectId),
  amount: Number(dto.amount),
  totalAmount: dto.totalAmount != null ? Number(dto.totalAmount) : undefined,
  paidAmount: Number(dto.paidAmount ?? 0),
  commissionTransfer: dto.commissionTransfer,
  commissionService: dto.commissionService,
  currency: {
    id: dto.currency?.id != null ? String(dto.currency.id) : undefined,
    code: dto.currency?.code,
    shortCode: dto.currency?.short_code ?? dto.currency?.code ?? '',
    name: dto.currency?.name,
    iconBase: dto.currency?.icon_base,
  },
  status: dto.status,
  address: dto.address
    ? {
        address: dto.address.address,
        qr: dto.address.qr,
      }
    : undefined,
  transactions: (dto.transactions ?? []).map(mapTransactionDto),
  createdAt: dto.createdAt,
  expiresAt: dto.expiresAt,
  paidAt: dto.paidAt,
});

export const mapInvoiceStatsDtoToEntity = (dto?: InvoiceStatsDto | null): InvoiceStats => ({
  total: Number(dto?.TOTAL ?? 0),
  paid: Number(dto?.STATUS_PAID ?? 0),
  underpaid: Number(dto?.STATUS_UNDERPAID ?? 0),
  pending: Number(dto?.STATUS_PENDING ?? 0),
});

export const getInvoicePayLink = (invoiceId: string): string =>
  buildPublicUrl(`/invoice/${invoiceId}`);
